import { PlayIcon, RefreshIcon } from '@heroicons/react/outline';
import { NodeProps } from '@xyflow/react';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { NodeExecutionStatus, Voice } from 'types';

import { useWorkflowStore } from '@/lib/workflows/store';
import { useIsMobile } from '@/hooks/useIsMobile';

import Button from '@/components/buttons/Button';
import VoiceSelection, {
  VOICE_PICKER_PANEL_CLASS,
} from '@/components/speech/VoicesModal';
import { SimpleTooltip } from '@/components/ui/tooltip';

import { DEFAULT_VOICE, VOICE_LIST_URL } from '@/constants/defaultVoice';
import { getVoiceImageUrl } from '@/utils/voices';

import { BaseNode, createInputHandle, createOutputHandle } from './BaseNode';
import { NodeActionsDropdown } from './NodeActionsDropdown';
import { TipTapPromptEditor } from '../TipTapPromptEditor';

type AvatarModel = 'premium' | 'standard';
type Resolution = '480p' | '720p';

interface TalkingVideoConfig {
  text: string;
  voice: string;
  voiceID: string;
  avatarModel: AvatarModel;
  resolution: Resolution;
  generateCaptions: boolean;
  captionColor: string;
}

interface TalkingVideoNodeProps extends NodeProps {
  onRunNode?: (nodeId: string) => void;
  nodeId?: string;
}

const WORDS_PER_MINUTE = 150;

const CAPTION_COLORS: { color: string; label: string }[] = [
  { color: '#39E508', label: 'Green' },
  { color: '#FFD700', label: 'Yellow' },
  { color: '#00D4FF', label: 'Cyan' },
  { color: '#FF3B30', label: 'Red' },
  { color: '#FF9500', label: 'Orange' },
  { color: '#FF2D55', label: 'Pink' },
  { color: '#AF52DE', label: 'Purple' },
  { color: '#FFFFFF', label: 'White' },
];

function estimateSpeechDurationSeconds(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  const cjkPattern = /[一-鿿぀-ゟ゠-ヿ가-힯]/g;
  const cjkMatches = trimmed.match(cjkPattern);
  const cjkCharCount = cjkMatches ? cjkMatches.length : 0;
  const totalCharCount = trimmed.replace(/\s/g, '').length;
  if (totalCharCount > 0 && cjkCharCount / totalCharCount > 0.3) {
    return totalCharCount / 4.5;
  }
  const wordCount = trimmed.split(/\s+/).length;
  return (wordCount / WORDS_PER_MINUTE) * 60;
}

export function TalkingVideoNode({
  id,
  data,
  selected,
  onRunNode,
  nodeId,
}: TalkingVideoNodeProps) {
  const { t } = useLocale('common');
  const isMobile = useIsMobile();
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const allNodes = useWorkflowStore((state) => state.nodes);
  const allEdges = useWorkflowStore((state) => state.edges);

  const [showSettings, setShowSettings] = useState<boolean>(
    !(typeof (data as { result?: unknown })?.result === 'string'),
  );
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  // Preset voices fetched from the API so the ~25k-line VOICES constant stays
  // out of the client bundle.
  const [voices, setVoices] = useState<Voice[]>([DEFAULT_VOICE]);
  const [voicesLoading, setVoicesLoading] = useState(true);

  useEffect(() => {
    fetch(VOICE_LIST_URL)
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d?.voices)) setVoices(d.voices);
      })
      .catch((e) => console.error('Failed to load voices', e))
      .finally(() => setVoicesLoading(false));
  }, []);

  const executionStatus = ((s) => {
    return typeof s === 'string' &&
      ['idle', 'running', 'success', 'error'].includes(s)
      ? (s as NodeExecutionStatus)
      : undefined;
  })((data as { status?: unknown })?.status);

  const isRunning = executionStatus === 'running';

  const config: TalkingVideoConfig = {
    text: '',
    voice: DEFAULT_VOICE.name,
    voiceID: DEFAULT_VOICE.voice_id,
    avatarModel: 'premium',
    resolution: '480p',
    generateCaptions: false,
    captionColor: '#39E508',
    ...((data.config as Partial<TalkingVideoConfig>) || {}),
  };

  // Detect inputs based on connected edges
  const incomingEdges = useMemo(
    () => allEdges.filter((edge) => edge.target === id),
    [allEdges, id],
  );

  const hasVideoInput = useMemo(
    () => incomingEdges.some((e) => e.targetHandle === 'video'),
    [incomingEdges],
  );
  const hasAudioInput = useMemo(
    () => incomingEdges.some((e) => e.targetHandle === 'audio'),
    [incomingEdges],
  );
  const hasTextInput = useMemo(
    () => incomingEdges.some((e) => e.targetHandle === 'text'),
    [incomingEdges],
  );

  // Auto-collapse settings when result becomes available
  useEffect(() => {
    if (
      typeof (data as { result?: unknown })?.result === 'string' &&
      showSettings &&
      !isRunning
    ) {
      setShowSettings(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.result]);

  const shouldShowResult =
    typeof (data as { result?: unknown })?.result === 'string' && !isRunning;

  const updateConfig = (updates: Partial<TalkingVideoConfig>) => {
    // Read the latest config straight from the store so consecutive calls
    // (e.g. setVoice + setVoiceID from VoicesModal) compose instead of
    // overwriting each other with a stale render-time snapshot.
    const currentNode = useWorkflowStore
      .getState()
      .nodes.find((n) => n.id === id);
    const currentConfig =
      (currentNode?.data?.config as Partial<TalkingVideoConfig>) || {};
    const nextData: Record<string, unknown> = {
      config: {
        ...currentConfig,
        ...updates,
      },
    };
    // Clear stale execution error so a fresh edit doesn't keep showing the
    // failure from a previous run.
    if ((currentNode?.data as { error?: unknown })?.error) {
      nextData.error = '';
    }
    updateNodeData(id, nextData);
  };

  const handleSetVoice = (voiceName: string) => {
    updateConfig({ voice: voiceName });
  };

  const handleSetVoiceID = (voiceID: string) => {
    updateConfig({ voiceID });
  };

  const handleTextChange = (value: string) => {
    updateConfig({ text: value });
  };

  const selectedVoice =
    voices.find((v) => v.voice_id === config.voiceID) || DEFAULT_VOICE;

  const rawError = (data as { error?: unknown })?.error;
  const errorText =
    typeof rawError === 'string'
      ? rawError.trim()
      : rawError != null
        ? String(rawError).trim()
        : '';

  // Connected text nodes for mention autocomplete
  const getConnectedTextNodes = useCallback(() => {
    const connectedEdges = allEdges.filter(
      (edge) => edge.target === id && edge.targetHandle === 'text',
    );

    return connectedEdges
      .map((edge) => {
        const sourceNode = allNodes.find((n) => n.id === edge.source);
        return {
          id: sourceNode?.id || '',
          label: (sourceNode?.data?.label as string) || 'Node',
        };
      })
      .filter((n) => n.id);
  }, [id, allNodes, allEdges]);

  // Credits per second based on input type, model and resolution
  const creditsPerSecond = useMemo(() => {
    if (hasVideoInput) {
      return config.resolution === '720p' ? 3 : 1;
    }
    if (config.avatarModel === 'standard') {
      return config.resolution === '720p' ? 2 : 1;
    }
    return config.resolution === '720p' ? 7 : 3;
  }, [hasVideoInput, config.avatarModel, config.resolution]);

  // Estimate credits based on text duration when audio not connected
  const estimatedCredits = useMemo(() => {
    if (hasAudioInput) {
      return null;
    }
    const textValue = config.text || '';
    if (!textValue.trim()) return 0;
    const seconds = estimateSpeechDurationSeconds(textValue);
    return Math.ceil(seconds * creditsPerSecond);
  }, [hasAudioInput, config.text, creditsPerSecond]);

  // Extract video URL for dropdown / preview
  const dataResult = (data as { result?: unknown })?.result;
  const videoUrl = useMemo(() => {
    if (typeof dataResult === 'string') return dataResult;
    if (dataResult && typeof dataResult === 'object') {
      return (dataResult as { videoUrl?: string }).videoUrl;
    }
    return undefined;
  }, [dataResult]);

  return (
    <>
      <BaseNode
        title={
          (typeof (data as { label?: unknown })?.label === 'string' &&
            (data as { label?: string }).label) ||
          t('Talking Video')
        }
        icon={
          <svg
            className='w-4 h-4 text-pink-600'
            fill='none'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path d='M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z' />
          </svg>
        }
        hasInput={false}
        hasOutput={false}
        status={executionStatus}
        selected={selected}
        nodeId={nodeId || id}
        onRunNode={onRunNode}
        customInputs={
          <>
            {createInputHandle('image', 'image', 25)}
            {createInputHandle('video', 'video', 45)}
            {createInputHandle('audio', 'audio', 65)}
            {createInputHandle('text', 'text', 85)}
          </>
        }
        customOutputs={createOutputHandle('video', 'video', 50)}
        hideToolbarActions
        customHeaderButtons={
          <>
            {data.result && (
              <SimpleTooltip
                title={showSettings ? t('Hide settings') : t('Show settings')}
                side='top'
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSettings(!showSettings);
                  }}
                  className='flex-shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors'
                >
                  <svg
                    className='w-5 h-5 text-gray-600 dark:text-gray-300'
                    fill='none'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
                    <path d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                  </svg>
                </button>
              </SimpleTooltip>
            )}
            <NodeActionsDropdown
              nodeId={nodeId || id}
              nodeTitle={(data.label as string) || t('Talking Video')}
              mediaUrl={videoUrl}
              mediaType='video'
              filename='talking-video.mp4'
            />
          </>
        }
      >
        <div className='w-[400px]'>
          {/* Settings Section */}
          <div
            className={`space-y-3 px-4 pt-3 pb-3 border-b border-gray-200 dark:border-gray-700 ${
              showSettings ? '' : 'hidden'
            }`}
          >
            {/* Inputs hint */}
            <div className='rounded-md bg-gray-50 dark:bg-slate-700/40 px-3 py-2 text-xs text-gray-600 dark:text-gray-300'>
              {t(
                'Connect an image or video for the talking subject, then either an audio clip or a text node with a voice selected below.',
              )}
            </div>

            {/* Avatar Model - only when image is the source */}
            {!hasVideoInput && (
              <div>
                <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  {t('Avatar Model')}
                  <SimpleTooltip
                    title={t(
                      'Premium offers higher quality. Standard is faster and cheaper.',
                    )}
                  >
                    <span className='ml-1 text-gray-400 cursor-help'>?</span>
                  </SimpleTooltip>
                </label>
                <div className='grid grid-cols-2 gap-2'>
                  <button
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation();
                      updateConfig({ avatarModel: 'premium' });
                    }}
                    className={`text-xs px-3 py-2 rounded-md border transition-colors ${
                      config.avatarModel === 'premium'
                        ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {t('Premium')}
                  </button>
                  <button
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation();
                      updateConfig({ avatarModel: 'standard' });
                    }}
                    className={`text-xs px-3 py-2 rounded-md border transition-colors ${
                      config.avatarModel === 'standard'
                        ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {t('Standard')}
                  </button>
                </div>
              </div>
            )}

            {/* Resolution */}
            <div>
              <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                {t('Resolution')}
              </label>
              <select
                value={config.resolution}
                onChange={(e) => {
                  e.stopPropagation();
                  updateConfig({
                    resolution: e.target.value as Resolution,
                  });
                }}
                onClick={(e) => e.stopPropagation()}
                className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
              >
                <option value='480p'>{t('480p')}</option>
                <option value='720p'>{t('720p')}</option>
              </select>
            </div>

            {/* Voice Selection - hidden when audio is provided */}
            {!hasAudioInput && (
              <div>
                <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  {t('Voice')}
                </label>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowVoiceModal(true);
                  }}
                  className='w-full flex items-center space-x-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-500 dark:hover:border-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
                >
                  <Image
                    src={getVoiceImageUrl(selectedVoice?.image)}
                    alt={selectedVoice?.name || t('Voice')}
                    width='50'
                    height='50'
                    className='rounded-lg'
                  />
                  <div className='flex-1 text-left'>
                    <p className='text-sm font-medium text-gray-900 dark:text-white'>
                      {selectedVoice?.name || t('Select Voice')}
                    </p>
                    {selectedVoice?.language && (
                      <p className='text-xs text-gray-500 dark:text-gray-400'>
                        {Array.isArray(selectedVoice.language)
                          ? selectedVoice.language[0]
                          : selectedVoice.language}
                        {selectedVoice.accent && ` (${selectedVoice.accent})`}
                      </p>
                    )}
                  </div>
                  <svg
                    className='w-5 h-5 text-gray-400 dark:text-gray-500'
                    fill='none'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path d='M9 5l7 7-7 7' />
                  </svg>
                </button>
              </div>
            )}

            {/* Text Input - hidden when audio is provided */}
            {!hasAudioInput && (
              <div>
                <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  {hasTextInput
                    ? t('Text (optional, overrides connected text)')
                    : t('Text (optional if connected)')}
                </label>
                <TipTapPromptEditor
                  value={config.text || ''}
                  onChange={handleTextChange}
                  connectedNodes={getConnectedTextNodes()}
                  placeholder={t(
                    'Enter the script... Type @ to reference connected nodes',
                  )}
                />
                <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
                  {t('Or connect text from another node')}
                </p>
              </div>
            )}

            {/* Audio info */}
            {hasAudioInput && (
              <div className='rounded-md border border-indigo-200 dark:border-indigo-800/60 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-2 text-xs text-indigo-700 dark:text-indigo-300'>
                {t(
                  'Using the connected audio clip. Voice selection and text are not needed.',
                )}
              </div>
            )}

            {/* Captions Toggle */}
            <div className='flex items-center justify-between'>
              <label className='block text-xs font-medium text-gray-700 dark:text-gray-300'>
                {t('Generate captions')}
                <SimpleTooltip
                  title={t(
                    'Automatically add TikTok-style captions to the video.',
                  )}
                >
                  <span className='ml-1 text-gray-400 cursor-help'>?</span>
                </SimpleTooltip>
              </label>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  updateConfig({ generateCaptions: !config.generateCaptions });
                }}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  config.generateCaptions
                    ? 'bg-primary-600'
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    config.generateCaptions
                      ? 'translate-x-[18px]'
                      : 'translate-x-[2px]'
                  }`}
                />
              </button>
            </div>

            {/* Caption color picker */}
            {config.generateCaptions && (
              <div>
                <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  {t('Highlight color')}
                </label>
                <div className='flex flex-wrap items-center gap-1.5'>
                  {CAPTION_COLORS.map(({ color, label }) => (
                    <SimpleTooltip key={color} title={t(label)}>
                      <button
                        type='button'
                        onClick={(e) => {
                          e.stopPropagation();
                          updateConfig({ captionColor: color });
                        }}
                        className={`relative h-6 w-6 rounded-full transition-all ${
                          color === '#FFFFFF'
                            ? 'border border-gray-200 dark:border-gray-600'
                            : ''
                        } ${
                          config.captionColor === color
                            ? 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-slate-900'
                            : 'hover:scale-110'
                        }`}
                        style={{
                          backgroundColor: color,
                          ...(config.captionColor === color
                            ? {
                                ['--tw-ring-color' as string]: color,
                              }
                            : {}),
                        }}
                        aria-label={label}
                      />
                    </SimpleTooltip>
                  ))}
                </div>
              </div>
            )}

            {/* Cost hint */}
            <div className='rounded-md bg-gray-50 dark:bg-slate-700/40 px-3 py-2 text-xs text-gray-600 dark:text-gray-300'>
              {(() => {
                const unit = (n: number) =>
                  n === 1 ? t('credit') : t('credits');
                const sourceLabel = hasVideoInput
                  ? t('Video upload')
                  : config.avatarModel === 'premium'
                    ? t('Premium')
                    : t('Standard');
                if (hasAudioInput) {
                  return `${t('Cost')}: ${creditsPerSecond} ${unit(creditsPerSecond)}/${t('sec of audio')} (${sourceLabel}, ${config.resolution})`;
                }
                if (estimatedCredits && estimatedCredits > 0) {
                  return `${t('Estimated')} ${estimatedCredits} ${unit(estimatedCredits)} (${creditsPerSecond} ${unit(creditsPerSecond)}/${t('sec')}, ${sourceLabel}, ${config.resolution})`;
                }
                return `${t('Cost is calculated from the final audio length')}: ${creditsPerSecond} ${unit(creditsPerSecond)}/${t('sec')} (${sourceLabel}, ${config.resolution})`;
              })()}
            </div>

            {/* Run Button */}
            <div className='pt-1'>
              <Button
                className='w-full justify-center'
                variant='outline'
                isLoading={isRunning}
                onClick={(e) => {
                  e.stopPropagation();
                  onRunNode?.(nodeId || id);
                }}
              >
                {data.result ? (
                  <>
                    <RefreshIcon className='w-4 h-4 mr-2' />
                    <span>{t('Regenerate')}</span>
                  </>
                ) : (
                  <>
                    <PlayIcon className='w-4 h-4 mr-2' />
                    <span>{t('Run')}</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Result Preview - Video */}
          {shouldShowResult && videoUrl && (
            <div className='p-3'>
              <div
                className={
                  showSettings ? 'w-full' : isLandscape ? 'w-[700px]' : ''
                }
              >
                <div className='relative group rounded-md overflow-hidden bg-black'>
                  <video
                    src={videoUrl}
                    controls
                    className='w-full h-auto object-cover'
                    autoPlay={!isMobile}
                    muted
                    preload={isMobile ? 'metadata' : 'auto'}
                    playsInline
                    loop
                    onLoadedMetadata={(e) => {
                      const video = e.currentTarget;
                      setIsLandscape(video.videoWidth > video.videoHeight);
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {errorText && (
            <div className='mx-3 mb-3 border-t border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-2 py-2 rounded'>
              <p className='text-xs text-red-600 dark:text-red-400'>
                {errorText}
              </p>
            </div>
          )}
        </div>
      </BaseNode>

      {/* Voice Selection Modal */}
      {showVoiceModal &&
        typeof document !== 'undefined' &&
        ReactDOM.createPortal(
          <div
            className='fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-[9999]'
            onClick={(e) => {
              e.stopPropagation();
              setShowVoiceModal(false);
            }}
          >
            <div
              className={`w-[95vw] overflow-hidden bg-white shadow-xl dark:bg-slate-900 ${VOICE_PICKER_PANEL_CLASS}`}
              onClick={(e) => e.stopPropagation()}
            >
              <VoiceSelection
                voices={voices}
                setVoice={handleSetVoice}
                setVoiceID={handleSetVoiceID}
                currentVoice={config.voice}
                currentVoiceId={config.voiceID}
                closeModal={() => setShowVoiceModal(false)}
                isLoading={voicesLoading}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
